import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../StorageHelper";

interface GetUserAnalyticsApisParams {
    cloudProvider: string;
    latestTenant: string;
    projectId: string;
    email: string;
}

interface UserAnalyticsApis {
    resourceApi: string;
    resolutionApi: string;
    permissionApi: string;
    suggestionApi: string;
    rawLogsAPI?: string;
    secretAnalyticsApi?: string;
}

export function getAnalyticsApis(params: any) {
    const { entityType } = params;
    if (entityType === "user") {
        return getUserAnalyticsApis(params);
    }


    return getNHIAnalyticsApis(params);
}


export const getUserAnalyticsApis = ({
    cloudProvider,
    latestTenant,
    projectId,
    email,
}: GetUserAnalyticsApisParams): UserAnalyticsApis | Record<string, never> => {
    const timeRangeCode = 7;

    if (cloudProvider === "aws") {
        return {
            resourceApi: `${NGROK}/api/aws/analytics/get-user-resource-usage?tenantName=${latestTenant}&accountId=${projectId}&timeRangeCode=7&arn=${email}`,
            resolutionApi: `${NGROK}/api/aws/analytics/get-user-analytics-resolutions?tenantName=${latestTenant}&accountId=${projectId}&timeRangeCode=7&arn=${email}`,
            permissionApi: `${NGROK}/api/aws/analytics/get-user-permission-usage?tenantName=${latestTenant}&accountId=${projectId}&timeRangeCode=7&arn=${email}`,
            suggestionApi: `${NGROK}/api/aws/analytics/get-user-analytics-suggestions?tenantName=${latestTenant}&accountId=${projectId}&timeRangeCode=7&arn=${email}`,
        };
    }

    if (cloudProvider === "azure") {
        const userId = encodeURIComponent(email);
        return {
            resourceApi: `${NGROK}/api/azure/analytics/get-user-resource-usage?tenantName=${latestTenant}&subscriptionId=${projectId}&timeRangeCode=${timeRangeCode}&userId=${userId}`,
            resolutionApi: `${NGROK}/api/azure/analytics/get-user-analytics-resolutions?tenantName=${latestTenant}&subscriptionId=${projectId}&timeRangeCode=${timeRangeCode}&userId=${userId}`,
            permissionApi: `${NGROK}/api/azure/analytics/get-user-permission-usage?tenantName=${latestTenant}&subscriptionId=${projectId}&timeRangeCode=${timeRangeCode}&userId=${userId}`,
            suggestionApi: `${NGROK}/api/azure/analytics/get-user-analytics-suggestions?tenantName=${latestTenant}&subscriptionId=${projectId}&timeRangeCode=${timeRangeCode}&userId=${userId}`,
        };
    }

    if (cloudProvider === "gcp") {
        return {
            resourceApi: `${NGROK}/api/gcp/analytics/get-user-resource-usage?tenantName=${latestTenant}&projectId=${projectId}&timeRangeCode=${timeRangeCode}&email=${email}`,
            resolutionApi: `${NGROK}/api/gcp/analytics/get-user-analytics-resolutions?tenantName=${latestTenant}&projectId=${projectId}&timeRangeCode=${timeRangeCode}&email=${email}`,
            permissionApi: `${NGROK}/api/gcp/analytics/get-user-permission-usage?tenantName=${latestTenant}&projectId=${projectId}&timeRangeCode=${timeRangeCode}&email=${email}`,
            suggestionApi: `${NGROK}/api/gcp/analytics/get-user-analytics-suggestions?tenantName=${latestTenant}&projectId=${projectId}&timeRangeCode=${timeRangeCode}&email=${email}`,
        };
    }

    return {};
};

interface NHIAnalyticsParams {
    cloudProvider: "GCP" | "AWS" | "AZURE" | string;
    latestTenant: string;
    projectId?: string;
    email?: string;
    cloudNHIType:
    | "TOKEN"
    | "SECRET"
    | "K8SERVICE"
    | "K8TOKEN"
    | "K8SECRET"
    | "OIDC"
    | string;
    details?: any;
    extrasdetails?: any;
}

export function getNHIAnalyticsApis({
    cloudProvider,
    latestTenant: tenantName,
    projectId,
    email,
    cloudNHIType,
    details = {},
    extrasdetails = {},
}: NHIAnalyticsParams) {
    const cloudType = cloudProvider.toUpperCase();
    const latestSubListRaw = myLocalStorage.getItem("latestSubList");
    const latestSubList = latestSubListRaw ? JSON.parse(latestSubListRaw) : null;
    const storedProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
    const accountId = myLocalStorage.getItem("selectedAwsAccount");
    const subscriptionId = latestSubList?.azureSubscriptionId;
    const resolvedProjectId =
        cloudType === "GCP"
            ? storedProjectId || projectId
            : cloudType === "AZURE"
                ? subscriptionId || projectId
                : accountId || projectId;


    const serviceAccountEmail = details.serviceAccountEmail;

    const principalId = details.principalId;
    const secretName = details.secretName;
    const nameSpace = details.nameSpace;

    const cloudTokenKeyId = details?.cloudTokenDetails?.keyId;
    const oidcARN = details?.oidcARN;

    const createApiUrl = (base: string, params: any) =>
        `${NGROK}${base}?${new URLSearchParams(params).toString()}`;

    // CloudId selection
    const cloudId =
        resolvedProjectId;

    // Common params
    const commonParams: any = {
        tenantName,
        timeRangeCode: 7,
        ...(cloudType === "GCP"
            ? { projectId: resolvedProjectId }
            : cloudType === "AZURE"
                ? { subscriptionId: resolvedProjectId }
                : { accountId: resolvedProjectId }),
    };

    // Email or User selection
    const emailOrUserId =
        cloudType === "GCP"
            ? { email: serviceAccountEmail }
            : cloudType === "AZURE"
                ? { userId: principalId || email || serviceAccountEmail }
                : { accountId: resolvedProjectId };

    const nameParam = { name: secretName };

    // Secret param logic
    const secretParam =
        cloudNHIType === "TOKEN"
            ? { tokenName: cloudTokenKeyId }
            : cloudType === "AWS"
                ? { arn: secretName }
                : { secretName };
    const secretEmailParam = {
        email: email || secretName,
    };

    let resourceApi = "";
    let permissionApi = "";
    let resolutionApi = "";
    let suggestionApi = "";
    let tokenLogsAPI = "";
    let tokenUsageAPI = "";
    let roleBindingURL = "";
    let rawLogsAPI = "";
    let secretAnalyticsApi = "";

    // -------------------------------------------------------------------
    // SWITCH CASE
    // -------------------------------------------------------------------
    switch (cloudNHIType) {
        case "TOKEN":
            resourceApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-tokens-resource-usage`,
                { ...commonParams, ...secretParam }
            );
            permissionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-tokens-permission-usage`,
                { ...commonParams, ...secretParam }
            );
            rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                tenantName,
                cloudId,
                cloudType,
                entity: cloudTokenKeyId,
            });
            break;

        case "SECRET":
            if (cloudType === "GCP") {
                secretAnalyticsApi = createApiUrl(
                    `/api/gcp/analytics/get-secrets-analytics`,
                    {
                        tenantName,
                        projectId: resolvedProjectId,
                        timeRangeCode: 7,
                        secretName,
                    }
                );
            } else {
                resourceApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-secrets-resource-usage`,
                    { ...commonParams, ...secretParam, ...secretEmailParam }
                );
                permissionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-secrets-permission-usage`,
                    { ...commonParams, ...secretParam, ...secretEmailParam }
                );
            }
            rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                tenantName,
                cloudId,
                cloudType,
                entity: secretName,
            });
            break;

        case "K8SERVICE":
            resourceApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-k8-service-account-resource-usage`,
                { ...commonParams, ...nameParam, nameSpace }
            );
            permissionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-k8-service-account-permission-usage`,
                { ...commonParams, ...nameParam, nameSpace }
            );
            rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                tenantName,
                cloudId,
                cloudType,
                entity: secretName,
            });
            break;

        case "K8TOKEN":
            resourceApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-k8-token-resource-usage`,
                { ...commonParams, ...nameParam, nameSpace }
            );
            permissionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-k8-token-permission-usage`,
                { ...commonParams, ...nameParam, nameSpace }
            );

            tokenLogsAPI = createApiUrl(`/api/k8/v1/getLogs`, {
                ...commonParams,
                name: secretName,
            });

            tokenUsageAPI = createApiUrl(`/api/k8/v1/getUsages`, {
                ...commonParams,
                name: secretName,
            });

            roleBindingURL = createApiUrl(`/api/k8/v1/getBindings`, {
                ...commonParams,
                name: secretName,
            });

            rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                tenantName,
                cloudId,
                cloudType,
                entity: secretName,
            });
            break;

        case "K8SECRET":
            resourceApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-k8-secret-resource-usage`,
                { ...commonParams, ...nameParam, nameSpace, ...secretEmailParam }
            );
            permissionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-k8-secret-permission-usage`,
                { ...commonParams, ...nameParam, nameSpace, ...secretEmailParam }
            );
            rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                tenantName,
                cloudId,
                cloudType,
                entity: secretName,
            });
            break;

        case "OIDC":
            resourceApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-oidc-resource-usage`,
                { ...commonParams, ...emailOrUserId, arn: oidcARN }
            );
            permissionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-oidc-permission-usage`,
                { ...commonParams, ...emailOrUserId, arn: oidcARN }
            );
            resolutionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-oidc-analytics-resolutions`,
                { ...commonParams, ...emailOrUserId, arn: oidcARN }
            );
            suggestionApi = createApiUrl(
                `/api/${cloudType.toLowerCase()}/analytics/get-oidc-analytics-suggestions`,
                { ...commonParams, ...emailOrUserId, arn: oidcARN }
            );

            rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                tenantName,
                cloudId,
                cloudType,
                entity: oidcARN,
            });
            break;

        // DEFAULT → IAM ROLE / USER
        default:
            if (details.cloudType === "AWS") {

                const arn = serviceAccountEmail;
                console.log("IAM ROLE NHI", arn);
                resourceApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-iam-role-resource-usage`,
                    { ...commonParams, ...emailOrUserId, arn }
                );

                permissionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-iam-role-permission-usage`,
                    { ...commonParams, ...emailOrUserId, arn }
                );

                resolutionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-iam-role-analytics-resolutions`,
                    { ...commonParams, ...emailOrUserId, arn }
                );

                suggestionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-iam-role-analytics-suggestions`,
                    { ...commonParams, ...emailOrUserId, arn }
                );

                rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                    tenantName,
                    cloudId,
                    cloudType,
                    entity: arn,
                });
            } else {
                // USER ANALYTICS
                resourceApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-user-resource-usage`,
                    { ...commonParams, ...emailOrUserId }
                );
                permissionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-user-permission-usage`,
                    { ...commonParams, ...emailOrUserId }
                );
                resolutionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-user-analytics-resolutions`,
                    { ...commonParams, ...emailOrUserId }
                );
                suggestionApi = createApiUrl(
                    `/api/${cloudType.toLowerCase()}/analytics/get-user-analytics-suggestions`,
                    { ...commonParams, ...emailOrUserId }
                );

                rawLogsAPI = createApiUrl(`/api/cloud/get-raw-logs`, {
                    tenantName,
                    cloudId,
                    cloudType,
                    entity:
                        emailOrUserId.email ||
                        emailOrUserId.userId ||
                        emailOrUserId.accountId,
                });
            }
    }

    return {
        resourceApi,
        permissionApi,
        resolutionApi,
        suggestionApi,
        rawLogsAPI,
        tokenLogsAPI,
        tokenUsageAPI,
        roleBindingURL,
        secretAnalyticsApi,
    };
}
